import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Trophy, Plus, Trash2, Pencil, FileText, Download,
  Calendar, Award, Users, School, Building2, GraduationCap, BookOpen, Loader2, Send, Search, ChevronDown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return d; }
};

const targetSummary = (c) => {
  const parts = [];
  parts.push(c.grade_number ? `صف ${c.grade_number}` : 'كل الصفوف');
  parts.push(c.subject_name || (c.subject_id ? 'مادة محددة' : 'كل المواد'));
  parts.push(c.school_name || (c.school_id ? 'مدرسة محددة' : 'كل المدارس'));
  parts.push(c.directorate_name || (c.directorate_id ? 'مديرية محددة' : 'كل المديريات'));
  return parts;
};

// ─────────────────────────────────────────────────────────────
// CONTEST MANAGER (Admin / Minister / Director)
// ─────────────────────────────────────────────────────────────
const EMPTY = {
  title: '', description: '', prize: '', grade_number: '', subject_id: '',
  school_id: '', directorate_id: '', start_date: '', deadline: '', allow_submissions: true,
};

export function ContestsManager({ headers, token, scoped = false, lightDialogs = false }) {
  const [contests, setContests] = useState([]);
  const [meta, setMeta] = useState({ grades: [], subjects: [], schools: [], directorates: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [questionFile, setQuestionFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subsContest, setSubsContest] = useState(null);

  const fetchContests = useCallback(() => {
    axios.get(`${API_URL}/api/contests/manage`, { headers })
      .then(r => setContests(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    fetchContests();
    axios.get(`${API_URL}/api/contests/meta`, { headers }).then(r => setMeta(r.data)).catch(() => {});
  }, [headers, fetchContests]);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setQuestionFile(null); setShowForm(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      title: c.title || '', description: c.description || '', prize: c.prize || '',
      grade_number: c.grade_number || '', subject_id: c.subject_id || '',
      school_id: c.school_id || '', directorate_id: c.directorate_id || '',
      start_date: c.start_date || '', deadline: c.deadline || '', allow_submissions: c.allow_submissions !== false,
    });
    setQuestionFile(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('عنوان المسابقة مطلوب'); return; }
    setSaving(true);
    try {
      const subj = meta.subjects.find(s => s.id === form.subject_id);
      const sch = meta.schools.find(s => s.id === form.school_id);
      const dir = meta.directorates.find(d => d.id === form.directorate_id);
      const payload = {
        ...form,
        grade_number: form.grade_number ? Number(form.grade_number) : null,
        subject_id: form.subject_id || null,
        subject_name: subj ? subj.name : '',
        school_id: form.school_id || null,
        school_name: sch ? sch.name : '',
        directorate_id: form.directorate_id || null,
        directorate_name: dir ? dir.name : '',
      };
      let contestId = editId;
      if (editId) {
        await axios.put(`${API_URL}/api/contests/${editId}`, payload, { headers });
      } else {
        const res = await axios.post(`${API_URL}/api/contests`, payload, { headers });
        contestId = res.data.id;
      }
      if (questionFile && contestId) {
        const fd = new FormData();
        fd.append('file', questionFile);
        await axios.post(`${API_URL}/api/contests/${contestId}/upload-question`, fd, {
          headers,
        });
      }
      toast.success(editId ? 'تم تحديث المسابقة' : 'تم إنشاء المسابقة');
      setShowForm(false);
      fetchContests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'حدث خطأ');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذه المسابقة وكل الحلول المرتبطة بها؟')) return;
    try {
      await axios.delete(`${API_URL}/api/contests/${id}`, { headers });
      toast.success('تم الحذف');
      fetchContests();
    } catch (e) { toast.error(e.response?.data?.detail || 'خطأ'); }
  };

  const subjectOptions = form.grade_number
    ? meta.subjects.filter(s => String(s.grade_number) === String(form.grade_number))
    : meta.subjects;

  if (loading) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;

  return (
    <div className="space-y-4" data-testid="contests-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2">
          <Trophy className="w-5 h-5" /> المسابقات
        </h2>
        <Button onClick={openCreate} data-testid="contest-create-btn"
          className="bg-[#4da6ff] hover:bg-[#3d96ef] text-white text-xs">
          <Plus className="w-4 h-4 ml-1" /> مسابقة جديدة
        </Button>
      </div>

      {contests.length === 0 ? (
        <div className="text-white/30 text-center py-12" data-testid="contests-empty">لا توجد مسابقات بعد. أنشئ أول مسابقة.</div>
      ) : (
        <div className="space-y-3">
          {contests.map(c => (
            <Card key={c.id} className="bg-white/5 border-white/10" data-testid={`contest-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-white font-bold">{c.title}</p>
                    {c.description && <p className="text-white/50 text-xs mt-1">{c.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {targetSummary(c).map((t, i) => (
                        <span key={i} className="text-[10px] text-white/60 bg-white/5 px-2 py-0.5 rounded-lg border border-white/10">{t}</span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-white/40">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> ينتهي: {fmtDate(c.deadline)}</span>
                      {c.prize && <span className="flex items-center gap-1 text-amber-400"><Award className="w-3 h-3" /> {c.prize}</span>}
                      {c.question_file && <span className="flex items-center gap-1 text-emerald-400"><FileText className="w-3 h-3" /> ملف الأسئلة مرفق</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setSubsContest(c)} data-testid={`contest-subs-${c.id}`}
                      className="text-[#4da6ff] text-xs bg-[#4da6ff]/10 hover:bg-[#4da6ff]/20">
                      <Users className="w-3.5 h-3.5 ml-1" /> {scoped ? 'حلول طلابي' : 'الحلول'} ({c.submission_count})
                    </Button>
                    {c.can_edit !== false && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="text-white/60 hover:text-white">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {c.can_edit === false && (
                      <span className="text-[10px] text-white/30 bg-white/5 px-2 py-1 rounded-lg">جارية</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className={`${lightDialogs ? 'tdlg ' : ''}bg-[#1a1a2e] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto`} dir="rtl">
          <DialogHeader><DialogTitle className="text-right">{editId ? 'تعديل المسابقة' : 'مسابقة جديدة'}</DialogTitle>
            <DialogDescription className="text-right text-white/40 text-xs">حدّد تفاصيل المسابقة والفئة المستهدفة وأرفق ملف الأسئلة.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {scoped && (
              <div className="text-xs text-[#4da6ff] bg-[#4da6ff]/10 border border-[#4da6ff]/20 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="contest-scope-note">
                <School className="w-4 h-4" /> ستظهر هذه المسابقة لطلاب مدرستك فقط.
              </div>
            )}
            <Field label="عنوان المسابقة *">
              <input data-testid="contest-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="ct-input" placeholder="مثال: مسابقة الرياضيات الكبرى" />
            </Field>
            <Field label="التفاصيل">
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="ct-input min-h-[70px]" placeholder="وصف المسابقة وشروطها" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="الصف المستهدف">
                <select value={form.grade_number} onChange={e => setForm({ ...form, grade_number: e.target.value, subject_id: '' })} className="ct-input" data-testid="contest-grade-select">
                  <option value="">كل الصفوف</option>
                  {meta.grades.map(g => <option key={g.number} value={g.number}>{g.name}</option>)}
                </select>
              </Field>
              <Field label="المادة">
                <select value={form.subject_id} onChange={e => setForm({ ...form, subject_id: e.target.value })} className="ct-input">
                  <option value="">كل المواد</option>
                  {subjectOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="المدرسة" className={scoped ? 'hidden' : ''}>
                <select value={form.school_id} onChange={e => setForm({ ...form, school_id: e.target.value })} className="ct-input" disabled={scoped}>
                  <option value="">كل المدارس</option>
                  {meta.schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="المديرية" className={scoped ? 'hidden' : ''}>
                <select value={form.directorate_id} onChange={e => setForm({ ...form, directorate_id: e.target.value })} className="ct-input" disabled={scoped}>
                  <option value="">كل المديريات</option>
                  {meta.directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="تاريخ البدء">
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="ct-input" />
              </Field>
              <Field label="الموعد النهائي">
                <input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className="ct-input" data-testid="contest-deadline-input" />
              </Field>
            </div>
            <Field label="الجائزة (اختياري)">
              <input value={form.prize} onChange={e => setForm({ ...form, prize: e.target.value })} className="ct-input" placeholder="مثال: شهادة تقدير + جائزة مالية" />
            </Field>
            <Field label="ملف الأسئلة (PDF / Word)">
              <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.txt" onChange={e => setQuestionFile(e.target.files[0])}
                className="ct-input text-xs file:bg-[#4da6ff]/20 file:text-[#4da6ff] file:border-0 file:rounded-lg file:px-3 file:py-1 file:ml-2" data-testid="contest-question-file" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.allow_submissions} onChange={e => setForm({ ...form, allow_submissions: e.target.checked })} />
              السماح للمستخدمين برفع حلولهم
            </label>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="bg-[#4da6ff] hover:bg-[#3d96ef] flex-1" data-testid="contest-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : null}
                {editId ? 'حفظ التعديلات' : 'إنشاء المسابقة'}
              </Button>
              <Button variant="ghost" onClick={() => setShowForm(false)} className="text-white/60">إلغاء</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {subsContest && <SubmissionsDialog contest={subsContest} token={token} onClose={() => setSubsContest(null)} headers={headers} lightDialogs={lightDialogs} />}
      <style>{`.ct-input{width:100%;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:0.75rem;padding:0.5rem 0.75rem;color:#fff;font-size:0.875rem;outline:none}.ct-input:focus{border-color:#4da6ff}.ct-input option{background:#1a1a2e}`}</style>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs text-white/50 mb-1">{label}</label>
      {children}
    </div>
  );
}

const ROLE_AR = {
  student: 'طالب', teacher: 'معلم', principal: 'مدير مدرسة',
  director: 'مدير مديرية', minister: 'وزير', parent: 'ولي أمر', admin: 'مدير النظام',
};

function SubmissionsDialog({ contest, token, onClose, lightDialogs = false }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/api/contests/${contest.id}/submissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error('فشل تحميل الحلول'));
  }, [contest.id, token]);

  const subs = data?.submissions || [];
  const answersVisible = data?.answers_visible !== false;
  const q = search.trim();
  const filtered = q
    ? subs.filter(s => [s.user_name, s.school_name, s.directorate_name].some(v => (v || '').includes(q)))
    : subs;

  // Group by directorate -> school
  const groups = {};
  filtered.forEach(s => {
    const dir = s.directorate_name || '—';
    const sch = s.school_name || '—';
    if (!groups[dir]) groups[dir] = {};
    if (!groups[dir][sch]) groups[dir][sch] = [];
    groups[dir][sch].push(s);
  });
  const dirNames = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'ar'));

  const schoolsCount = new Set(filtered.map(s => s.school_name || '—')).size;
  const dirCount = dirNames.length;

  const dl = (s, i) => `${API_URL}/api/contests/${contest.id}/submissions/${s.user_id}/download/${i}?token=${token}`;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={`${lightDialogs ? 'tdlg ' : ''}bg-[#1a1a2e] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto`} dir="rtl">
        <DialogHeader><DialogTitle className="text-right">حلول: {contest.title}</DialogTitle>
          <DialogDescription className="text-right text-white/40 text-xs">الحلول مُجمَّعة حسب المديرية ثم المدرسة لتسهيل المتابعة.</DialogDescription>
        </DialogHeader>

        {!data ? <div className="text-white/30 text-center py-8">جارٍ التحميل...</div> :
          subs.length === 0 ? <div className="text-white/30 text-center py-8" data-testid="contest-subs-empty">لا توجد حلول مرسلة بعد.</div> :
          <div className="space-y-3" data-testid="contest-subs-list">
            {!answersVisible && (
              <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2" data-testid="answers-hidden-note">
                هذه مسابقة من إنشاء الإدارة — يمكنك الاطّلاع على طلابك المشاركين فقط، أما الإجابات فغير متاحة للعرض.
              </div>
            )}
            {/* Summary */}
            <div className="grid grid-cols-3 gap-2">
              <Stat icon={Users} value={subs.length} label="إجمالي الحلول" color="text-[#4da6ff]" />
              <Stat icon={School} value={schoolsCount} label="مدارس مشاركة" color="text-emerald-400" />
              <Stat icon={Building2} value={dirCount} label="مديريات" color="text-purple-400" />
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-white/30 absolute right-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)} data-testid="contest-subs-search"
                placeholder="ابحث باسم الطالب أو المدرسة أو المديرية..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pr-9 pl-3 py-2 text-sm text-white outline-none focus:border-[#4da6ff]" />
            </div>

            {filtered.length === 0 ? <div className="text-white/30 text-center py-6">لا نتائج مطابقة.</div> :
              dirNames.map(dir => {
                const schools = groups[dir];
                const dirTotal = Object.values(schools).reduce((a, arr) => a + arr.length, 0);
                const isCollapsed = collapsed[dir];
                return (
                  <div key={dir} className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`subs-dir-${dir}`}>
                    <button onClick={() => setCollapsed(c => ({ ...c, [dir]: !c[dir] }))}
                      className="w-full flex items-center justify-between p-3 hover:bg-white/5">
                      <span className="flex items-center gap-2 text-sm font-bold text-purple-300">
                        <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                        <Building2 className="w-4 h-4" /> {dir}
                      </span>
                      <span className="text-[11px] text-white/40">{dirTotal} حل · {Object.keys(schools).length} مدرسة</span>
                    </button>
                    {!isCollapsed && (
                      <div className="px-3 pb-3 space-y-3">
                        {Object.keys(schools).sort((a, b) => a.localeCompare(b, 'ar')).map(sch => (
                          <div key={sch}>
                            <div className="flex items-center gap-2 text-xs font-medium text-emerald-300 mb-1.5 mt-1">
                              <School className="w-3.5 h-3.5" /> {sch}
                              <span className="text-white/30">({schools[sch].length})</span>
                            </div>
                            <div className="space-y-1.5">
                              {schools[sch].map(s => (
                                <div key={s.id} className="rounded-lg bg-white/5 border border-white/10 p-2.5">
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-white text-sm font-medium">{s.user_name}</span>
                                      <span className="text-[10px] text-white/50 bg-white/5 px-1.5 py-0.5 rounded">{ROLE_AR[s.user_role] || s.user_role}</span>
                                      {s.grade_number && <span className="text-[10px] text-[#4da6ff] bg-[#4da6ff]/10 px-1.5 py-0.5 rounded">صف {s.grade_number}</span>}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 items-center">
                                      <span className="text-[10px] text-white/30">{fmtDate(s.submitted_at)}</span>
                                      {answersVisible ? (s.files || []).map((f, i) => (
                                        <a key={i} href={dl(s, i)} target="_blank" rel="noreferrer"
                                          className="text-[10px] text-[#4da6ff] bg-[#4da6ff]/10 px-2 py-0.5 rounded flex items-center gap-1 hover:bg-[#4da6ff]/20">
                                          <Download className="w-3 h-3" /> {f.filename}
                                        </a>
                                      )) : (
                                        <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                                          ✓ شارك{s.files_count ? ` · ${s.files_count} ملف` : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {answersVisible && s.note && <p className="text-white/60 text-xs mt-1.5 bg-white/5 p-2 rounded">{s.note}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 text-center">
      <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-white/40 text-[10px]">{label}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CONTEST PARTICIPANT SECTION (shown on every user profile/dashboard)
// ─────────────────────────────────────────────────────────────
export function ContestsSection({ headers, token }) {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchContests = useCallback(() => {
    axios.get(`${API_URL}/api/contests`, { headers })
      .then(r => setContests(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => { fetchContests(); }, [fetchContests]);

  if (loading) return <div className="text-white/30 text-center py-8">جارٍ تحميل المسابقات...</div>;

  return (
    <div className="space-y-3" data-testid="contests-section">
      <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2">
        <Trophy className="w-5 h-5" /> المسابقات
      </h2>
      {contests.length === 0 ? (
        <div className="text-white/30 text-center py-8" data-testid="contests-section-empty">لا توجد مسابقات متاحة لك حالياً.</div>
      ) : (
        contests.map(c => <ParticipantCard key={c.id} contest={c} headers={headers} token={token} onChanged={fetchContests} />)
      )}
    </div>
  );
}

function ParticipantCard({ contest: c, headers, token, onChanged }) {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async () => {
    if (files.length === 0 && !note.trim()) { toast.error('أرفق ملفاً أو اكتب ملاحظة'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      files.forEach((f, i) => fd.append(`file_${i}`, f));
      if (note.trim()) fd.append('note', note);
      await axios.post(`${API_URL}/api/contests/${c.id}/submit`, fd, {
        headers,
      });
      toast.success('تم إرسال حلك بنجاح');
      setFiles([]); setNote('');
      if (fileRef.current) fileRef.current.value = '';
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الإرسال');
    } finally { setSending(false); }
  };

  return (
    <Card className="bg-white/5 border-white/10" data-testid={`contest-participant-${c.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <p className="text-white font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-400" /> {c.title}</p>
            {c.description && <p className="text-white/60 text-xs mt-1">{c.description}</p>}
          </div>
          {c.prize && <span className="text-[11px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded-lg flex items-center gap-1"><Award className="w-3 h-3" /> {c.prize}</span>}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {c.grade_number && <Tag icon={GraduationCap}>صف {c.grade_number}</Tag>}
          {c.subject_name && <Tag icon={BookOpen}>{c.subject_name}</Tag>}
          {c.school_name && <Tag icon={School}>{c.school_name}</Tag>}
          {c.directorate_name && <Tag icon={Building2}>{c.directorate_name}</Tag>}
          <Tag icon={Calendar}>ينتهي: {fmtDate(c.deadline)}</Tag>
        </div>

        <div className="flex flex-wrap gap-2">
          {c.question_file && (
            <a href={`${API_URL}/api/contests/${c.id}/download-question?token=${token}`} target="_blank" rel="noreferrer"
              data-testid={`contest-download-q-${c.id}`}
              className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-emerald-500/20">
              <FileText className="w-3.5 h-3.5" /> تحميل ملف الأسئلة
            </a>
          )}
          {c.my_submission && (
            <span className="text-xs text-[#4da6ff] bg-[#4da6ff]/10 px-3 py-1.5 rounded-lg flex items-center gap-1">
              ✓ أرسلت حلك ({(c.my_submission.files || []).length} ملف)
            </span>
          )}
        </div>

        {c.allow_submissions !== false && (
          <div className="border-t border-white/10 pt-3 space-y-2">
            <p className="text-white/50 text-xs">{c.my_submission ? 'أضف ملفات أخرى لحلك:' : 'أرسل حلك:'}</p>
            <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
              onChange={e => setFiles(Array.from(e.target.files))} data-testid={`contest-file-${c.id}`}
              className="w-full text-xs text-white/60 file:bg-[#4da6ff]/20 file:text-[#4da6ff] file:border-0 file:rounded-lg file:px-3 file:py-1 file:ml-2" />
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="ملاحظة (اختياري)"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#4da6ff] min-h-[50px]" />
            <Button onClick={handleSubmit} disabled={sending} data-testid={`contest-submit-${c.id}`}
              className="bg-[#4da6ff] hover:bg-[#3d96ef] text-white text-xs">
              {sending ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Send className="w-3.5 h-3.5 ml-1" />}
              إرسال الحل
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Tag({ icon: Icon, children }) {
  return (
    <span className="text-[10px] text-white/60 bg-white/5 px-2 py-0.5 rounded-lg border border-white/10 flex items-center gap-1">
      <Icon className="w-3 h-3" /> {children}
    </span>
  );
}
